import React from "react";
import { Badge, Box, Typography, alpha, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getNavIcon } from "../navIcons";
import { getCatalogItemById } from "../../../constants/navCatalog";
import { dashIconWellSx, getDashAccent } from "../myDashboardStyles";
import { usePendingTimeTrackingCount } from "../../../hooks/usePendingTimeTrackingCount";

export default function ShortcutWidget({ widget, editing }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const catalog = getCatalogItemById(widget.catalogId);
  const Icon = getNavIcon(catalog?.icon);
  const label = catalog ? t(catalog.labelKey) : widget.catalogId;
  const route = widget.route || catalog?.route;
  const accent = getDashAccent(
    catalog?.id || widget.catalogId,
    catalog?.section,
    theme.palette.mode,
  );
  const isControlHorario =
    catalog?.id === "control-horario" || widget.catalogId === "control-horario";
  const { total: pendingHorario } = usePendingTimeTrackingCount();
  const showHorarioBadge = isControlHorario && pendingHorario > 0;

  const handleClick = () => {
    if (editing || !route) return;
    navigate(route);
  };

  return (
    <Box
      onClick={handleClick}
      role={editing ? undefined : "button"}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        p: 1.5,
        cursor: editing ? "default" : "pointer",
        borderRadius: 2,
        "&:hover .shortcut-icon": editing
          ? undefined
          : {
              transform: "translateY(-3px) scale(1.05)",
              boxShadow: `0 14px 28px ${alpha(accent, 0.35)}, inset 0 1px 0 ${alpha("#fff", theme.palette.mode === "dark" ? 0.12 : 0.95)}`,
            },
        "&:hover .shortcut-label": editing ? undefined : { color: accent },
      }}
    >
      <Box
        className="shortcut-icon"
        sx={[
          (th) => ({
            ...dashIconWellSx(th, accent),
            width: { xs: 44, sm: 56 },
            height: { xs: 44, sm: 56 },
            borderRadius: { xs: "14px", sm: "18px" },
          }),
          {
            transition:
              "transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.28s ease",
          },
        ]}
      >
        <Badge
          badgeContent={pendingHorario}
          invisible={!showHorarioBadge}
          max={99}
          overlap="circular"
          sx={{
            "& .MuiBadge-badge": {
              backgroundColor: "#e53935",
              color: "#fff",
              fontWeight: 800,
              fontSize: "0.7rem",
              minWidth: 20,
              height: 20,
              borderRadius: "50%",
              border: `2px solid ${theme.palette.background.paper}`,
            },
          }}
        >
          <Icon sx={{ fontSize: { xs: 22, sm: 26 } }} />
        </Badge>
      </Box>
      <Typography
        className="shortcut-label"
        variant="body2"
        fontWeight={700}
        textAlign="center"
        sx={{
          lineHeight: 1.2,
          letterSpacing: 0.15,
          px: 0.5,
          transition: "color 0.2s ease",
          fontSize: { xs: "0.72rem", sm: "0.8rem" },
          wordBreak: "break-word",
          hyphens: "auto",
          maxWidth: "100%",
          color: "text.primary",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
